import { httpBase } from "./auth.js";

/**
 * Is the client in this tab the one the server is serving? A tab left open
 * across a redeploy keeps its old bundle until F5; this is that press,
 * automatic. `__BUILD_ID__` (stamped at build) is compared with `/build.json`
 * (written by the same build, served `no-cache`). Asked at boot, on every
 * return to the menu, and when a backgrounded tab comes back in the menu —
 * never in a raid. ONE RELOAD PER SERVER BUILD, remembered in `sessionStorage`,
 * so a stale proxy cannot loop it.
 */

const RELOADED_FOR_KEY = "efr.reloadedFor";

/** The id this bundle carries; "dev" (never stale) under the dev server. */
function localBuildId(): string {
  return typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev";
}

/**
 * Whether the server's id calls for a reload of a client carrying `local`.
 * `null` (no `build.json`) and "dev" mean nothing to compare, never stale.
 */
export function isStale(local: string, remote: string | null): boolean {
  if (remote === null || remote.length === 0) return false;
  if (local === "dev") return false;
  return local !== remote;
}

/** `/build.json`'s id, or `null` when there is no such file or it is unreadable. */
async function fetchRemoteBuildId(serverUrl: string): Promise<string | null> {
  try {
    const res = await fetch(`${httpBase(serverUrl)}/build.json`, { cache: "no-store" });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    const id = (body as { buildId?: unknown } | null)?.buildId;
    return typeof id === "string" ? id : null;
  } catch {
    return null;
  }
}

/**
 * Compares this tab's build with the server's and reloads if behind. `true`
 * when a reload was started, `false` when current or unknowable.
 */
export async function reloadIfStale(serverUrl: string): Promise<boolean> {
  const local = localBuildId();
  if (local === "dev") return false;
  const remote = await fetchRemoteBuildId(serverUrl);
  if (!isStale(local, remote)) return false;

  let alreadyReloadedFor: string | null = null;
  try {
    alreadyReloadedFor = sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    // Storage refused (privacy mode): reload once regardless — a loop needs the
    // server to keep serving the old page while answering `build.json` with a
    // new id.
  }
  if (alreadyReloadedFor === remote) return false;
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, remote as string);
  } catch {
    // See above.
  }
  console.info(`client build ${local} is behind server build ${remote}; reloading`);
  window.location.reload();
  return true;
}

/**
 * Re-runs the check whenever the tab comes back into view and `inMenu()` says a
 * reload is harmless. Returns the unsubscribe.
 */
export function watchForStaleBuild(serverUrl: string, inMenu: () => boolean): () => void {
  const onVisible = (): void => {
    if (document.visibilityState !== "visible" || !inMenu()) return;
    void reloadIfStale(serverUrl);
  };
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
  };
}
